import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../ui/SafeIcon'

const { FiSearch, FiTrendingUp, FiPackage, FiShield, FiGlobe } = FiIcons

function HeroSection() {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const navigate = useNavigate()

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  const features = [
    { icon: FiPackage, text: t('hero.feature.factoryPrices'), color: 'text-blue-600' },
    { icon: FiShield, text: t('hero.feature.tradeAssurance'), color: 'text-green-600' },
    { icon: FiGlobe, text: t('hero.feature.globalShipping'), color: 'text-purple-600' },
    { icon: FiTrendingUp, text: t('hero.feature.products'), color: 'text-orange-600' }
  ]

  const popularSearches = [
    'Smartphone', 'Laptop', 'T-Shirt', 'Shoes', 'Watch', 'Headphones'
  ]

  return (
    <section className="bg-gradient-to-br from-orange-50 via-white to-blue-50 py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Main Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {t('hero.title.prefix')} <span className="text-[#FF6600]">{t('hero.title.highlight')}</span> {t('hero.title.suffix')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {t('hero.subtitle')}
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-2xl mx-auto mb-8"
        >
          <form onSubmit={handleSearch} className="relative">
            <div className="relative flex items-center bg-white rounded-lg shadow-xl border-2 border-[#FF6600] overflow-hidden">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('search.placeholder')}
                className="flex-1 px-6 py-4 text-lg focus:outline-none"
              />
              <button
                type="submit"
                className="px-8 py-4 bg-[#FF6600] text-white font-bold text-lg hover:bg-[#e55c00] transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiSearch} className="w-6 h-6" />
                <span className="hidden sm:inline">{t('search.button')}</span>
              </button>
            </div>
          </form>
        </motion.div>

        {/* Popular Searches */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-12"
        >
          <p className="text-sm text-gray-500 mb-3">{t('hero.popular')}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {popularSearches.map((search, index) => (
              <Link
                key={index}
                to={`/search?q=${search}`}
                className="px-4 py-2 bg-white border border-gray-300 rounded-full text-sm hover:border-[#FF6600] hover:text-[#FF6600] transition-colors"
              >
                {search}
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6"
        >
          {features.map((feature, index) => (
            <div key={index} className="flex flex-col items-center space-y-2">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md">
                <SafeIcon icon={feature.icon} className={`w-6 h-6 ${feature.color}`} />
              </div>
              <span className="text-sm font-medium text-gray-700 text-center">{feature.text}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default HeroSection